/**
 * Build a sound, cast-free narrower/type-guard for a string enum.
 *
 * `no-unsafe-enum-comparison` fires when a plain `string` is compared to an enum
 * member. For a value that genuinely arrives as a free string (SDK/DOM/URL/free-text
 * column), the honest fix is to narrow it to the enum via a runtime membership check
 * — not an `as` cast. This factory does that with ZERO `as`: the `Set<string>` lets
 * `.has()` accept a string, and the `value is E[keyof E]` type predicate performs the
 * narrowing (TS trusts the predicate, which is backed by the real Set lookup).
 *
 * Usage:
 *   const reportingType = enumNarrow(OngoingReportingType)
 *   reportingType.as(file.type) === OngoingReportingType.AR   // enum === enum, no cast
 *   if (reportingType.is(x)) { ... x is OngoingReportingType ... }
 */
export const enumNarrow = <E extends Record<string, string>>(enumObject: E) => {
  const values = new Set<string>(Object.values(enumObject))
  const is = (value: string | null | undefined): value is E[keyof E] =>
    value != null && values.has(value)
  const as = (value: string | null | undefined): E[keyof E] | undefined =>
    is(value) ? value : undefined
  return { is, as }
}
